import React, { useState } from "react";
import {
  Button,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getAdminUsage, getCurrentMonth } from "./usageApi";
import { planLabel } from "./UsageParts";
import { formatMonth, monthRange, usagePercent } from "./usageFormat";

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Pick a month and download every tenant's usage for it as CSV
// (SUPER_ADMIN, ADMIN). Opened from the tenant list, which passes the first
// month that has data (earliestMonth from GET /admin/usage).
const UsageExportModal = ({ earliestMonth, onClose }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const currentMonth = getCurrentMonth();
  const [month, setMonth] = useState(currentMonth);
  const [exporting, setExporting] = useState(false);

  const monthLabel = (value) =>
    value === currentMonth
      ? lng === "en"
        ? `${formatMonth(value, lng)} (${t("usage.currentMonth")})`
        : `${formatMonth(value, lng)}（${t("usage.currentMonth")}）`
      : formatMonth(value, lng);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await getAdminUsage(month);
      const header = [
        t("usage.columns.tenantName"),
        t("usage.columns.portalId"),
        t("usage.columns.month"),
        t("usage.columns.plan"),
        t("usage.columns.messageCount"),
        t("usage.columns.monthlyQuota"),
        t("usage.columns.usageRate"),
        t("usage.columns.contractEnd"),
        t("usage.columns.sendingStatus"),
      ];
      const rows = data.tenants.map((tenant) => {
        const percent = usagePercent(tenant.messageCount, tenant.quota);
        return [
          tenant.name,
          tenant.portalId,
          data.month,
          planLabel(t, tenant.plan),
          tenant.messageCount,
          tenant.quota ?? t("usage.notSet"),
          percent === null
            ? t("usage.notSet")
            : t("usage.usageRateValue", { percent }),
          tenant.contractEnd || t("usage.notSet"),
          t(`tenants.details.sendingControl.status.${tenant.sendingStatus}`),
        ];
      });
      // BOM so Excel opens the UTF-8 file with Chinese text intact.
      const csv =
        "﻿" +
        [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${t("usage.exportFileName", { month: data.month })}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      toast.error(t("usage.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} centered style={{ maxWidth: 480 }}>
      <ModalHeader toggle={onClose}>{t("usage.export.heading")}</ModalHeader>
      <ModalBody>
        <Label htmlFor="usage-export-month">{t("usage.monthLabel")}</Label>
        <Input
          id="usage-export-month"
          type="select"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {monthRange(earliestMonth || currentMonth, currentMonth).map((value) => (
            <option key={value} value={value}>
              {monthLabel(value)}
            </option>
          ))}
        </Input>
        <FormText>{t("usage.export.hint")}</FormText>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          {t("common.actions.cancel")}
        </Button>
        <Button color="primary" onClick={handleExport} disabled={exporting}>
          {exporting ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="ri-download-2-line me-1"></i>
          )}
          {t("common.actions.exportCsv")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UsageExportModal;
